// Meta tag utilities for SEO and social sharing
import api from '../services/api';

// Cache for site logo to avoid repeated API calls
let siteLogoCache = null;
let siteLogoCacheTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Get site logo from admin settings
export const getSiteLogo = async () => {
    // Return cached logo if still valid
    if (siteLogoCache && siteLogoCacheTime && (Date.now() - siteLogoCacheTime < CACHE_DURATION)) {
        return siteLogoCache;
    }
    
    try {
        const response = await api.get('/admin/settings/public');
        if (response.data.success && response.data.data.site_logo) {
            siteLogoCache = response.data.data.site_logo;
            siteLogoCacheTime = Date.now();
            return siteLogoCache;
        }
    } catch (error) {
        console.log('Could not fetch site logo from admin settings:', error);
    }
    
    // Fallback to default og-image
    return 'https://thedeepwave.com/og-image.jpg';
};

export const updateMetaTag = (property, content, type = 'property') => {
    if (!content) return;
    
    let selector = type === 'name' ? `meta[name="${property}"]` : `meta[property="${property}"]`;
    let tag = document.querySelector(selector);
    
    if (!tag) {
        tag = document.createElement('meta');
        if (type === 'name') {
            tag.name = property;
        } else {
            tag.property = property;
        }
        document.head.appendChild(tag);
    }
    tag.content = content;
};

export const updatePageMeta = async (options = {}) => {
    const {
        title = 'The Deep Wave',
        description = 'The Deep Wave - Creative Community Platform for Artists and Music Lovers',
        keywords = 'the deep wave, music, artists, community, creative platform',
        ogType = 'website',
        ogImage = null, // Will use site logo if not provided
        ogImageAlt = 'The Deep Wave - Creative Community Platform',
        twitterCard = 'summary_large_image',
        author = 'The Deep Wave',
        canonical = null
    } = options;
    
    // Get site logo if no custom image provided
    const finalOgImage = ogImage || await getSiteLogo();

    // Update page title
    document.title = title.includes('| The Deep Wave') ? title : `${title} | The Deep Wave`;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
        metaDescription.content = description;
    }

    // Update or create keywords meta tag
    updateMetaTag('keywords', keywords, 'name');

    // Update Open Graph tags
    updateMetaTag('og:title', title.includes('| The Deep Wave') ? title : `${title} | The Deep Wave`);
    updateMetaTag('og:description', description);
    updateMetaTag('og:url', canonical || window.location.href);
    updateMetaTag('og:type', ogType);
    updateMetaTag('og:site_name', 'The Deep Wave');
    updateMetaTag('og:image', finalOgImage);
    updateMetaTag('og:image:alt', ogImageAlt);

    // Twitter Card tags
    updateMetaTag('twitter:card', twitterCard, 'name');
    updateMetaTag('twitter:title', title.includes('| The Deep Wave') ? title : `${title} | The Deep Wave`, 'name');
    updateMetaTag('twitter:description', description, 'name');
    updateMetaTag('twitter:image', finalOgImage, 'name');
    updateMetaTag('twitter:image:alt', ogImageAlt, 'name');

    // Additional SEO tags
    updateMetaTag('robots', 'index, follow', 'name');
    updateMetaTag('author', author, 'name');
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0', 'name');
    
    // Add canonical URL if provided
    if (canonical) {
        let canonicalTag = document.querySelector('link[rel="canonical"]');
        if (!canonicalTag) {
            canonicalTag = document.createElement('link');
            canonicalTag.rel = 'canonical';
            document.head.appendChild(canonicalTag);
        }
        canonicalTag.href = canonical;
    }
};

export const updateAudioMeta = async (audio) => {
    if (!audio) return;
    
    const title = audio.title || 'Audio Track';
    const artist = audio.display_name || audio.username || 'Unknown Artist';
    const description = audio.description || `Listen to "${title}" by ${artist} on The Deep Wave`;
    const ogImage = audio.cover_art_url || null; // Will use site logo if null
    
    await updatePageMeta({
        title: `${title} by ${artist}`,
        description,
        keywords: `${title}, ${artist}, music, audio, the deep wave, streaming`,
        ogType: 'music.song',
        ogImage,
        ogImageAlt: `${title} by ${artist}`,
        author: artist
    });
    
    // Music-specific meta tags
    updateMetaTag('music:song', title);
    updateMetaTag('music:musician', artist);
    if (audio.duration) {
        updateMetaTag('music:duration', audio.duration);
    }
};

export const updateVideoMeta = async (video) => {
    if (!video) return;
    
    const title = video.title || 'Video';
    const creator = video.display_name || video.username || 'Unknown Creator';
    const description = video.description || `Watch "${title}" by ${creator} on The Deep Wave`;
    const ogImage = video.thumbnail_url || null; // Will use site logo if null
    
    await updatePageMeta({
        title: `${title} by ${creator}`,
        description,
        keywords: `${title}, ${creator}, video, content, the deep wave, streaming`,
        ogType: 'video.other',
        ogImage,
        ogImageAlt: `${title} by ${creator}`,
        author: creator
    });
    
    // Video-specific meta tags
    updateMetaTag('og:video', video.file_url);
    updateMetaTag('og:video:type', 'video/mp4');
    if (video.duration) {
        updateMetaTag('og:video:duration', video.duration);
    }
};

export const updateImageMeta = async (image) => {
    if (!image) return;
    
    const title = image.title || 'Artwork';
    const artist = image.display_name || image.username || 'Unknown Artist';
    const description = image.description || `View "${title}" by ${artist} on The Deep Wave`;
    const ogImage = image.file_url || null; // Will use site logo if null
    
    await updatePageMeta({
        title: `${title} by ${artist}`,
        description,
        keywords: `${title}, ${artist}, artwork, image, art, the deep wave, gallery`,
        ogType: 'article',
        ogImage,
        ogImageAlt: title,
        author: artist
    });
};

export const updateProfileMeta = async (profile) => {
    if (!profile) return;
    
    try {
        // Fetch site settings for dynamic title
        const response = await api.get('/site-settings');
        let siteTitle = '';
        let siteBranding = '';
        
        if (response.data.success) {
            const data = response.data.data;
            siteTitle = data.site_title || '';
            siteBranding = data.site_branding || '';
        }
        
        const displayName = profile.displayName || profile.display_name || profile.username || 'User';
        const description = profile.bio 
            ? `${displayName} - ${profile.bio.substring(0, 150)}${profile.bio.length > 150 ? '...' : ''} | The Deep Wave community member.`
            : `Meet ${displayName}, a member of The Deep Wave community. Discover their music, artwork, and posts.`;
        const ogImage = profile.profilePictureUrl || profile.profile_picture_url || null;
        
        // Construct dynamic title
        const pageTitle = siteTitle && siteBranding 
            ? `${displayName}'s Profile - 🌊 ${siteTitle} - ${siteBranding}`
            : `${displayName}'s Profile`;
        
        await updatePageMeta({
            title: pageTitle,
            description,
            keywords: `${displayName}, ${profile.username}, artist, musician, profile, the deep wave`,
            ogType: 'profile',
            ogImage,
            ogImageAlt: `${displayName}'s profile picture`,
            author: displayName
        });
        
        // Profile-specific meta tags
        updateMetaTag('profile:username', profile.username);
        if (profile.displayName || profile.display_name) {
            updateMetaTag('profile:first_name', profile.displayName || profile.display_name);
        }
    } catch (error) {
        console.error('Error updating profile meta tags:', error);
        // Fallback to basic profile meta if API call fails
        const displayName = profile.displayName || profile.display_name || profile.username || 'User';
        const description = profile.bio || `${displayName}'s profile on The Deep Wave`;
        const ogImage = profile.profilePictureUrl || profile.profile_picture_url || null;
        
        await updatePageMeta({
            title: `${displayName}'s Profile`,
            description,
            keywords: `${displayName}, ${profile.username}, artist, musician, profile, the deep wave`,
            ogType: 'profile',
            ogImage,
            ogImageAlt: `${displayName}'s profile picture`,
            author: displayName
        });
    }
};

export const updateLikedMediaMeta = async (profile, username) => {
    const displayName = profile?.displayName || profile?.display_name || username;
    const description = `Browse everything ${displayName} has liked on The Deep Wave — music, videos, and artwork from the community.`;
    const ogImage = profile?.profilePictureUrl || profile?.profile_picture_url || null;

    await updatePageMeta({
        title: `${displayName}'s Liked Media`,
        description,
        keywords: `${displayName}, ${username}, liked media, favorites, likes, the deep wave`,
        ogType: 'website',
        ogImage,
        ogImageAlt: `${displayName}'s profile picture`,
        author: displayName,
        canonical: `https://thedeepwave.com/profile/${username}/liked`
    });
};

export const updatePostMeta = async (post) => {
    if (!post) return;
    
    const title = post.content?.substring(0, 100) + '...' || 'Post';
    const author = post.author_display_name || post.author_username || 'Unknown User';
    const description = post.content || `A post by ${author} on The Deep Wave`;
    const ogImage = post.attachments?.[0]?.file_url || null; // Will use site logo if null
    
    await updatePageMeta({
        title: `${title} by ${author}`,
        description,
        keywords: `post, ${author}, community, social, the deep wave`,
        ogType: 'article',
        ogImage,
        ogImageAlt: 'Post on The Deep Wave',
        author
    });
};